import React, { useState } from 'react';
import { FaPlus, FaTimes, FaTrash, FaLayerGroup, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

// ==========================================
// 1. KONFIGURASI STANDAR WORLD TAEKWONDO (WT)
// ==========================================
const WT_CATEGORIES = {
    cadet_m: { divName: 'Cadet', gender: 'Lelaki', min: 12, max: 14, limits: [33, 37, 41, 45, 49, 53, 57, 61, 65] },
    cadet_f: { divName: 'Cadet', gender: 'Perempuan', min: 12, max: 14, limits: [29, 33, 37, 41, 44, 47, 51, 55, 59] },
    junior_m: { divName: 'Junior', gender: 'Lelaki', min: 15, max: 17, limits: [45, 48, 51, 55, 59, 63, 68, 73, 78] },
    junior_f: { divName: 'Junior', gender: 'Perempuan', min: 15, max: 17, limits: [42, 44, 46, 49, 52, 55, 59, 63, 68] },
    senior_m: { divName: 'Senior', gender: 'Lelaki', min: 18, max: 0, limits: [54, 58, 63, 68, 74, 80, 87] },
    senior_f: { divName: 'Senior', gender: 'Perempuan', min: 18, max: 0, limits: [46, 49, 53, 57, 62, 67, 73] }
};

// Daftar opsi untuk merender Checkbox WT dengan ringkas
const WT_OPTIONS = [
    { id: 'cadet_m', label: 'Cadet Putra', group: 'Cadet (12-14 Thn)' },
    { id: 'cadet_f', label: 'Cadet Putri', group: 'Cadet (12-14 Thn)' },
    { id: 'junior_m', label: 'Junior Putra', group: 'Junior (15-17 Thn)' },
    { id: 'junior_f', label: 'Junior Putri', group: 'Junior (15-17 Thn)' },
    { id: 'senior_m', label: 'Senior Putra', group: 'Senior (18+ Thn)' },
    { id: 'senior_f', label: 'Senior Putri', group: 'Senior (18+ Thn)' },
];


const TabMasterData = ({ masterData, handleMasterChange, setMasterData }) => {
    // --- STATE FORM MANUAL ---
    const [newBelt, setNewBelt] = useState("");
    const [newDivName, setNewDivName] = useState("");
    const [newDivMin, setNewDivMin] = useState("");
    const [newDivMax, setNewDivMax] = useState("");
    const [newWeightClass, setNewWeightClass] = useState("");
    
    // --- STATE NAVIGASI TAB KELAS BERAT ---
    const [selectedDivision, setSelectedDivision] = useState(masterData?.divisions?.[0]?.name || 'Senior');
    const [selectedGender, setSelectedGender] = useState('Lelaki');
    
    // --- STATE GENERATOR WT ---
    const [selectedWT, setSelectedWT] = useState(['senior_m', 'senior_f']);

    // ==========================================
    // 2. FUNGSI JADWAL & ARENA
    // ==========================================
    const handleDaysChange = (e) => {
        const totalHari = parseInt(e.target.value) || 1;
        let arrayArena = [...(masterData.arenasPerDay || [3])];
        
        while (arrayArena.length < totalHari) arrayArena.push(3); // Tambah hari baru = default 3 arena
        if (arrayArena.length > totalHari) arrayArena.length = totalHari; // Hapus sisa hari
        
        setMasterData({ ...masterData, days: totalHari, arenasPerDay: arrayArena });
    };

    const handleArenaChange = (indexHari, jumlahArena) => {
        let arrayArena = [...(masterData.arenasPerDay || [])];
        arrayArena[indexHari] = parseInt(jumlahArena) || 1;
        setMasterData({ ...masterData, arenasPerDay: arrayArena });
    };

    // ==========================================
    // 3. FUNGSI GENERATOR WT OTOMATIS
    // ==========================================
    const toggleWTCheckbox = (id) => {
        setSelectedWT(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const generateWTSettings = () => {
        if (selectedWT.length === 0) return toast.warning("Pilih minimal satu kelas WT.");

        let mergedDivisions = new Map(masterData.divisions?.map(d => [d.name, d]) || []);
        let mergedWeightClasses = { ...(masterData.weight_classes || {}) };

        selectedWT.forEach(wtKey => {
            const dataWT = WT_CATEGORIES[wtKey];
            
            // 1. Buat Divisi (Hanya jika belum ada)
            if (!mergedDivisions.has(dataWT.divName)) {
                mergedDivisions.set(dataWT.divName, { name: dataWT.divName, min_age: dataWT.min, max_age: dataWT.max });
            }

            // 2. Buat Teks Kelas Berat (Under 54 kg, dll)
            let arrayKelasBerat = dataWT.limits.map(limit => `Under ${limit} kg`);
            arrayKelasBerat.push(`Over ${dataWT.limits[dataWT.limits.length - 1]} kg`);

            // 3. Gabungkan ke State Kelas Berat
            if (!mergedWeightClasses[dataWT.divName]) mergedWeightClasses[dataWT.divName] = {};
            mergedWeightClasses[dataWT.divName][dataWT.gender] = arrayKelasBerat;
        });

        setMasterData({ ...masterData, divisions: Array.from(mergedDivisions.values()), weight_classes: mergedWeightClasses });
        toast.success("Standar WT berhasil diterapkan!");
    };

    // ==========================================
    // 4. FUNGSI MANUAL (DIVISI, SABUK, KELAS)
    // ==========================================
    const addBelt = () => {
        if (!newBelt) return;
        setMasterData({ ...masterData, belt_levels: [...(masterData.belt_levels || []), newBelt] });
        setNewBelt("");
    };

    const removeBelt = (idx) => {
        setMasterData({ ...masterData, belt_levels: masterData.belt_levels.filter((_, i) => i !== idx) });
    };

    const addDivision = () => {
        if (!newDivName || !newDivMin || !newDivMax) return;
        const newDiv = { name: newDivName, min_age: parseInt(newDivMin), max_age: parseInt(newDivMax) };
        setMasterData({ ...masterData, divisions: [...(masterData.divisions || []), newDiv] });
        setNewDivName(""); setNewDivMin(""); setNewDivMax("");
    };

    const removeDivision = (idx) => {
        setMasterData({ ...masterData, divisions: masterData.divisions.filter((_, i) => i !== idx) });
    };

    const addWeightClass = () => {
        if (!newWeightClass) return;
        let classesObj = { ...(masterData.weight_classes || {}) };
        if (!classesObj[selectedDivision]) classesObj[selectedDivision] = {};
        if (!classesObj[selectedDivision][selectedGender]) classesObj[selectedDivision][selectedGender] = [];
        
        classesObj[selectedDivision][selectedGender].push(newWeightClass);
        setMasterData({ ...masterData, weight_classes: classesObj });
        setNewWeightClass("");
    };

    const removeWeightClass = (idx) => {
        let classesObj = { ...masterData.weight_classes };
        classesObj[selectedDivision][selectedGender] = classesObj[selectedDivision][selectedGender].filter((_, i) => i !== idx);
        setMasterData({ ...masterData, weight_classes: classesObj });
    };

    // Data List Kelas Berat saat ini (untuk ditampilkan di tabel manual)
    const currentClassList = masterData?.weight_classes?.[selectedDivision]?.[selectedGender] || [];


    // ==========================================
    // 5. RENDER ANTARMUKA (UI)
    // ==========================================
    return (
        <div className="card border-0 shadow-sm animate-fade-in" style={{ borderRadius: '20px' }}>
            <div className="card-body p-4">
                <h5 className="fw-bold text-primary mb-4">Master Data Teknis</h5>

                {/* --- SEKSI 1: JADWAL EVENT --- */}
                <div className="row g-4 mb-5">
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Tahun Acuan Umur</label>
                        <input type="number" className="form-control bg-light" name="event_year" value={masterData.event_year || ''} onChange={handleMasterChange} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Total Hari Event</label>
                        <input type="number" className="form-control bg-light" min="1" value={masterData.days || 1} onChange={handleDaysChange} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label fw-bold mb-2">Jumlah Arena per Hari</label>
                        <div className="d-flex flex-wrap gap-2">
                            {(masterData.arenasPerDay || [3]).map((jumlah, idxHari) => (
                                <div key={idxHari} className="bg-light p-2 rounded border text-center" style={{ width: '80px' }}>
                                    <small className="fw-bold text-muted">Hari {idxHari + 1}</small>
                                    <input type="number" min="1" className="form-control form-control-sm text-center fw-bold text-primary mt-1" value={jumlah} onChange={(e) => handleArenaChange(idxHari, e.target.value)} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <hr className="text-muted opacity-25" />

                {/* --- SEKSI 2: GENERATOR WT --- */}
                <div className="p-4 bg-primary bg-opacity-10 rounded-4 mb-5 border border-primary border-opacity-25">
                    <h6 className="fw-bold text-primary mb-3"><FaLayerGroup className="me-2" /> Auto-Generate Kelas WT</h6>
                    <div className="row g-3 mb-3">
                        {/* Kelompokkan berdasarkan 'group' dari opsi WT yang kita buat di atas */}
                        {['Cadet (12-14 Thn)', 'Junior (15-17 Thn)', 'Senior (18+ Thn)'].map(groupName => (
                            <div className="col-md-4" key={groupName}>
                                <div className="bg-white p-3 rounded shadow-sm h-100">
                                    <h6 className="fw-bold text-dark border-bottom pb-1 small">{groupName}</h6>
                                    {WT_OPTIONS.filter(opt => opt.group === groupName).map(opt => (
                                        <div className="form-check" key={opt.id}>
                                            <input className="form-check-input" type="checkbox" id={opt.id} checked={selectedWT.includes(opt.id)} onChange={() => toggleWTCheckbox(opt.id)} />
                                            <label className="form-check-label small" htmlFor={opt.id}>{opt.label}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button type="button" className="btn btn-primary fw-bold" onClick={generateWTSettings}>
                        <FaCheckCircle className="me-2"/> Terapkan Kelas Standar WT
                    </button>
                </div>

                {/* --- SEKSI 3: DIVISI UMUR (MANUAL) --- */}
                <div className="mb-5">
                    <label className="form-label fw-bold">Aturan Kategori Umur (Divisi)</label>
                    <table className="table table-bordered align-middle text-center small">
                        <thead className="bg-light">
                            <tr><th>Nama Divisi</th><th>Min Umur</th><th>Max Umur (0=DST)</th><th>Aksi</th></tr>
                        </thead>
                        <tbody>
                            {(masterData.divisions || []).map((div, idx) => (
                                <tr key={idx}>
                                    <td className="fw-bold">{div.name}</td>
                                    <td>{div.min_age}</td>
                                    <td>{div.max_age}</td>
                                    <td><button type="button" className="btn btn-sm text-danger" onClick={() => removeDivision(idx)}><FaTrash /></button></td>
                                </tr>
                            ))}
                            <tr>
                                <td><input type="text" className="form-control form-control-sm" placeholder="Divisi Baru" value={newDivName} onChange={e => setNewDivName(e.target.value)} /></td>
                                <td><input type="number" className="form-control form-control-sm" placeholder="Min" value={newDivMin} onChange={e => setNewDivMin(e.target.value)} /></td>
                                <td><input type="number" className="form-control form-control-sm" placeholder="Max" value={newDivMax} onChange={e => setNewDivMax(e.target.value)} /></td>
                                <td><button type="button" className="btn btn-sm btn-primary" onClick={addDivision}><FaPlus /></button></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <hr className="text-muted opacity-25" />

                {/* --- SEKSI 4: SABUK --- */}
                <div className="mb-5">
                    <label className="form-label fw-bold">Daftar Tingkatan Sabuk</label>
                    <div className="input-group mb-3" style={{ maxWidth: '400px' }}>
                        <input type="text" className="form-control" placeholder="Tambah sabuk..." value={newBelt} onChange={(e) => setNewBelt(e.target.value)} />
                        <button type="button" className="btn btn-primary" onClick={addBelt}><FaPlus /></button>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {(masterData.belt_levels || []).map((belt, idx) => (
                            <span key={idx} className="badge bg-light text-dark border d-flex gap-2 p-2">{belt} <FaTimes className="text-danger" style={{cursor:'pointer'}} onClick={() => removeBelt(idx)} /></span>
                        ))}
                    </div>
                </div>

                <hr className="text-muted opacity-25" />

                {/* --- SEKSI 5: KELAS BERAT (MANUAL) --- */}
                <div>
                    <label className="form-label fw-bold mb-3">Editor Kelas Berat Manual</label>
                    <div className="card bg-light border-0">
                        <div className="row g-0">
                            <div className="col-md-3 border-end p-3 bg-white">
                                <div className="nav flex-column nav-pills gap-1">
                                    {(masterData.divisions || []).map((div, idx) => (
                                        <button key={idx} type="button" className={`nav-link text-start btn-sm fw-bold ${selectedDivision === div.name ? 'active' : 'text-secondary'}`} onClick={() => setSelectedDivision(div.name)}>{div.name}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="col-md-9 p-3">
                                <div className="d-flex gap-2 mb-3">
                                    <button type="button" className={`btn btn-sm px-4 rounded-pill fw-bold ${selectedGender === 'Lelaki' ? 'btn-primary' : 'btn-outline-secondary bg-white'}`} onClick={() => setSelectedGender('Lelaki')}>Putra</button>
                                    <button type="button" className={`btn btn-sm px-4 rounded-pill fw-bold ${selectedGender === 'Perempuan' ? 'btn-danger' : 'btn-outline-secondary bg-white'}`} onClick={() => setSelectedGender('Perempuan')}>Putri</button>
                                </div>
                                <div className="d-flex flex-column gap-2 mb-3">
                                    {currentClassList.map((cls, idx) => (
                                        <div key={idx} className="input-group input-group-sm w-50">
                                            <input type="text" className="form-control" value={cls} readOnly />
                                            <button type="button" className="btn btn-outline-danger" onClick={() => removeWeightClass(idx)}><FaTrash /></button>
                                        </div>
                                    ))}
                                    {currentClassList.length === 0 && <small className="text-muted fst-italic">Kosong. Gunakan Auto-Generate WT di atas.</small>}
                                </div>
                                <div className="input-group input-group-sm w-50">
                                    <input type="text" className="form-control" placeholder="Under 45 kg" value={newWeightClass} onChange={(e) => setNewWeightClass(e.target.value)} />
                                    <button type="button" className="btn btn-success text-white" onClick={addWeightClass}><FaPlus /> Tambah</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default TabMasterData;